import type { DeploymentTask, Ticket } from '../../lib/api'

export interface GatewayServiceDefinition {
  adapterType:
    | 'service_nmi'
    | 'service_authorize_net'
    | 'service_agms_pay'
    | 'terminal_valor'
    | 'terminal_dejavoo'
  name: string
  description: string
  provisionable: boolean
}

export interface MerchantEquipmentAsset {
  id: string
  merchant_id: string
  application_id: string | null
  asset_type: 'terminal' | 'pinpad' | 'pos' | 'mobile' | 'other'
  make: string | null
  model: string | null
  serial_number: string | null
  terminal_id: string | null
  tracking_number: string | null
  status: 'ready_to_build' | 'ordered' | 'building' | 'verification' | 'shipped' | 'active' | 'inactive' | 'returned' | 'replaced'
  notes: string | null
  created_at: string
  added_by?: string | { id: string; name: string } | null
}

export const GATEWAY_SERVICES: GatewayServiceDefinition[] = [
  {
    adapterType: 'service_nmi',
    name: 'NMI',
    description: 'Online and card-not-present gateway processing.',
    provisionable: true,
  },
  {
    adapterType: 'service_authorize_net',
    name: 'Authorize.net',
    description: 'Authorize.net reseller gateway account.',
    provisionable: true,
  },
  {
    adapterType: 'service_agms_pay',
    name: 'AGMS Pay',
    description: 'Internal AGMS Pay product slot.',
    provisionable: false,
  },
  {
    adapterType: 'terminal_valor',
    name: 'Valor',
    description: 'Valor terminal onboarding.',
    provisionable: false,
  },
  {
    adapterType: 'terminal_dejavoo',
    name: 'Dejavoo',
    description: 'Dejavoo terminal onboarding.',
    provisionable: true,
  },
]

export const EQUIPMENT_ASSET_TYPES: Array<{ value: MerchantEquipmentAsset['asset_type']; label: string }> = [
  { value: 'terminal', label: 'Terminal' },
  { value: 'pinpad', label: 'PIN pad' },
  { value: 'pos', label: 'POS' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'other', label: 'Other' },
]

export const EQUIPMENT_ASSET_STATUSES: Array<{ value: MerchantEquipmentAsset['status']; label: string }> = [
  { value: 'ready_to_build', label: 'Ready to build' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'building', label: 'Building' },
  { value: 'verification', label: 'Verification' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'returned', label: 'Returned' },
  { value: 'replaced', label: 'Replaced' },
]

export function chargebackStatusClass(status: string): string {
  switch (status) {
    case 'open':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'responded':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'won':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'lost':
      return 'border-gray-300 bg-gray-100 text-gray-700'
    case 'expired':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    default:
      return 'border-gray-200 bg-gray-50 text-gray-600'
  }
}

export function deploymentStatusClass(status: DeploymentTask['status'] | 'not_provisioned' | 'coming_soon'): string {
  switch (status) {
    case 'queued':
      return 'border-gray-200 bg-gray-50 text-gray-700'
    case 'in_progress':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'completed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'failed':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'cancelled':
      return 'border-gray-300 bg-gray-100 text-gray-700'
    case 'manual':
      return 'border-amber-200 bg-amber-50 text-amber-800'
    case 'coming_soon':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    default:
      return 'border-gray-200 bg-white text-gray-600'
  }
}

export function ticketPriorityClass(priority: Ticket['priority']): string {
  switch (priority) {
    case 'urgent':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'high':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'normal':
      return 'border-blue-200 bg-blue-50 text-blue-700'
  }
}

export function ticketStatusClass(status: Ticket['status']): string {
  switch (status) {
    case 'open':
      return 'border-sky-200 bg-sky-50 text-sky-700'
    case 'in_progress':
      return 'border-violet-200 bg-violet-50 text-violet-700'
    case 'resolved':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'closed':
      return 'border-gray-300 bg-gray-100 text-gray-700'
  }
}

export function gatewayStatusLabel(task: DeploymentTask | undefined, service: GatewayServiceDefinition): string {
  if (!service.provisionable) {
    return service.adapterType === 'terminal_valor' || service.adapterType === 'terminal_dejavoo'
      ? 'Coming soon — pending credentials'
      : 'Coming soon'
  }
  if (!task) return 'Not provisioned'
  if (task.status === 'in_progress') return 'In progress'
  if (task.status === 'cancelled') return 'Cancelled'
  return task.status.charAt(0).toUpperCase() + task.status.slice(1)
}

export function equipmentAssetLabel(value: string): string {
  return EQUIPMENT_ASSET_TYPES.find((type) => type.value === value)?.label ?? value
}

export function truncate(value: string, max = 80): string {
  return value.length > max ? `${value.slice(0, max)}...` : value
}

export function daysToDeadlineLabel(responseDueAt: string | null): string {
  if (!responseDueAt) return 'No due date'

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(`${responseDueAt}T00:00:00`)
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / 86400000)

  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`
  if (diffDays === 0) return 'Due today'
  if (diffDays === 1) return 'Due tomorrow'
  return `${diffDays}d left`
}
